const DRAFT_MAX_AGE_MS =
    7 * 24 * 60 * 60 * 1000;

const DRAFT_SAVE_DELAY_MS =
    500;

const draftStorageKey = (
    conversationId
) =>
    `festfriends:message-draft:${conversationId}`;

const parseDraft = (
    rawDraft
) => {
    if (! rawDraft) {
        return null;
    }

    try {
        const parsed =
            JSON.parse(rawDraft);

        if (
            typeof parsed?.body
            !== 'string'
            || typeof parsed?.savedAt
            !== 'number'
        ) {
            return null;
        }

        if (
            Date.now()
            - parsed.savedAt
            > DRAFT_MAX_AGE_MS
        ) {
            return null;
        }

        return parsed;
    } catch {
        return null;
    }
};

const initializeMessageDraft = () => {
    const composer =
        document.querySelector(
            '[data-message-composer]'
        );

    if (! composer) {
        return;
    }

    const conversationId =
        composer.dataset
            .conversationId;

    const textarea =
        composer.querySelector(
            'textarea[name="body"]'
        );

    const status =
        composer.querySelector(
            '[data-message-draft-status]'
        );

    const restoredNotice =
        composer.querySelector(
            '[data-message-draft-restored]'
        );

    const clearButtons =
        composer.querySelectorAll(
            '[data-message-draft-clear]'
        );

    if (
        ! conversationId
        || ! textarea
    ) {
        return;
    }

    const storageKey =
        draftStorageKey(
            conversationId
        );

    let saveTimer = null;

    const setStatus = (
        message
    ) => {
        if (status) {
            status.textContent =
                message;
        }
    };

    const setClearButtonsVisible = (
        visible
    ) => {
        clearButtons.forEach(
            (button) => {
                button.classList.toggle(
                    'hidden',
                    ! visible
                );
            }
        );
    };

    const clearStoredDraft = () => {
        window.clearTimeout(
            saveTimer
        );

        localStorage.removeItem(
            storageKey
        );

        setStatus('');
        setClearButtonsVisible(
            false
        );

        restoredNotice
            ?.classList
            .add('hidden');

        restoredNotice
            ?.classList
            .remove('flex');
    };

    const persistDraft = () => {
        const body =
            textarea.value;

        if (
            body.trim()
            === ''
        ) {
            clearStoredDraft();

            return;
        }

        localStorage.setItem(
            storageKey,
            JSON.stringify({
                body,
                savedAt:
                    Date.now(),
            })
        );

        setStatus(
            'Draft saved on this device'
        );

        setClearButtonsVisible(
            true
        );
    };

    const scheduleSave = () => {
        window.clearTimeout(
            saveTimer
        );

        setStatus(
            textarea.value.trim()
                === ''
                ? ''
                : 'Saving draft…'
        );

        saveTimer =
            window.setTimeout(
                persistDraft,
                DRAFT_SAVE_DELAY_MS
            );
    };

    const rawDraft =
        localStorage.getItem(
            storageKey
        );

    const draft =
        parseDraft(
            rawDraft
        );

    if (
        rawDraft
        && ! draft
    ) {
        localStorage.removeItem(
            storageKey
        );
    }

    /*
     * Server-rendered old input wins over a local draft. This matters if
     * Laravel has returned a validation error and preserved the user's
     * submitted text.
     */
    if (
        draft
        && textarea.value.trim()
            === ''
    ) {
        textarea.value =
            draft.body;

        textarea.dispatchEvent(
            new Event(
                'input',
                {
                    bubbles: true,
                }
            )
        );

        setStatus(
            'Draft restored from this device'
        );

        setClearButtonsVisible(
            true
        );

        restoredNotice
            ?.classList
            .remove('hidden');

        restoredNotice
            ?.classList
            .add('flex');
    } else if (
        draft
        && textarea.value.trim()
            !== ''
    ) {
        setClearButtonsVisible(
            true
        );
    }

    textarea.addEventListener(
        'input',
        scheduleSave
    );

    clearButtons.forEach(
        (button) => {
            button.addEventListener(
                'click',
                () => {
                    textarea.value = '';

                    textarea.dispatchEvent(
                        new Event(
                            'input',
                            {
                                bubbles: true,
                            }
                        )
                    );

                    clearStoredDraft();

                    textarea.focus();
                }
            );
        }
    );

    /*
     * message-ajax.js resets the composer only after the server has
     * successfully accepted the message, so reset is the safe signal
     * for clearing the saved draft.
     */
    composer.addEventListener(
        'reset',
        () => {
            window.setTimeout(
                clearStoredDraft,
                0
            );
        }
    );

    window.addEventListener(
        'beforeunload',
        () => {
            window.clearTimeout(
                saveTimer
            );

            if (
                textarea.value.trim()
                !== ''
            ) {
                persistDraft();
            }
        }
    );
};

if (
    document.readyState
    === 'loading'
) {
    document.addEventListener(
        'DOMContentLoaded',
        initializeMessageDraft
    );
} else {
    initializeMessageDraft();
}
